<template>
  <div
    class="cart-sidebar-row"
    :class="{
      'cart-sidebar-row--arriving': isArriving,
      'cart-sidebar-row--arriving-free': isArriving && isFree
    }"
  >
    <div class="cart-sidebar-row__thumbnail">
      <!-- `loading="lazy"` because the flyout is mounted on every route and hidden by a transform rather
           than `v-if` — without it, every cart bundle's thumbnail is fetched on every page load whether
           or not the customer opens the cart. -->
      <img
        v-if="imageUrl"
        :src="imageUrl"
        :alt="title"
        class="cart-sidebar-row__image"
        loading="lazy"
        draggable="false"
      />
    </div>
    <div class="cart-sidebar-row__content">
      <div class="cart-sidebar-row__heading">
        <div class="cart-sidebar-row__text">
          <div class="cart-sidebar-row__title-line">
            <span class="cart-sidebar-row__title">{{ title }}</span>
            <span
              v-if="isFree"
              class="cart-sidebar-row__free-pill"
              >{{ orderSummaryContent.freeLabel }}</span
            >
          </div>
          <div
            v-if="descriptionHtml"
            class="cart-sidebar-row__description"
            v-html="descriptionHtml"
          />
          <!-- Ordered to match the checkout row (`CartBundleItem`): package contents, then the
               customer's selections, then `extendedInfo` via `description`. -->
          <div
            v-for="selection in selections"
            :key="selection.label"
            class="cart-sidebar-row__selection"
          >
            <span>{{ selection.label }}:&nbsp;</span>
            <span>{{ selection.value }}</span>
            <span v-if="selection.upchargeFormatted">&nbsp;{{ selection.upchargeFormatted }}</span>
          </div>
          <div
            v-if="description"
            class="cart-sidebar-row__description"
          >
            {{ description }}
          </div>
        </div>
        <slot name="actions" />
      </div>
      <div class="cart-sidebar-row__price">
        <slot name="price" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import { CartBundleSelectionItemData } from '@/types/CartBundleSelection'

import { orderSummaryContent } from '@/content/orderSummary'

/**
 * Presentational cart-flyout row — Figma desktop `15980:29645` / mobile `15927:54459`, with the
 * reward treatment at `15980:29670` / `16066:33304`.
 *
 * Shared by the real cart-bundle row and the synthesized Free Shipping row (Gap 5), which are
 * visually identical but have different data behind them. Purely presentational: no store access, no
 * mutation. The price and any actions come in through slots, so a caller with no price component
 * (Free Shipping) and one with a remove button (cart bundles) both fit.
 *
 * Descriptions are props rather than a slot because scoped styles don't reach slotted content, and
 * both callers need the same type treatment on it.
 */
export default defineComponent({
  name: 'AppCartSidebarRow',
  setup() {
    return { orderSummaryContent }
  },
  props: {
    title: {
      type: String,
      required: true
    },
    imageUrl: {
      type: String as PropType<string | null>,
      default: null
    },
    /** Rich-text description, pre-sanitized by the caller. */
    descriptionHtml: {
      type: String as PropType<string | null>,
      default: null
    },
    /** Plain-text description; renders below `descriptionHtml` when both are present. */
    description: {
      type: String as PropType<string | null>,
      default: null
    },
    /**
     * The customer's selections on the bundle (e.g. "Paper Type: Glossy Paper"), one line each.
     *
     * A prop rather than a slot for the same reason the descriptions are: scoped styles don't reach
     * slotted content, and these need the row's description treatment. Derived by the caller from
     * the `cartBundleSelections` commerce-store getter, which keeps the wording identical to the
     * checkout row.
     */
    selections: {
      type: Array as PropType<CartBundleSelectionItemData[]>,
      default: () => []
    },
    /** Shows the green FREE pill beside the title — unlocked rewards and the Free Shipping row. */
    isFree: {
      type: Boolean,
      default: false
    },
    /**
     * Plays the arrival flash — a tinted background settling to the resting one.
     *
     * **The row is not actually arriving.** By the time the sequence runs, the cart response has
     * landed and this row's data has been held back for presentation; the 4,000ms beat reveals it. So
     * this is a deliberate fiction and worth naming as one, because it has a consequence: the flash
     * says "this just appeared" about something that has existed for four seconds, and if the reward's
     * thumbnail is still cold the row flashes around an empty box. The tint colour follows `isFree`,
     * which is what separates a reward's arrival from a paid item's.
     */
    isArriving: {
      type: Boolean,
      default: false
    }
  }
})
</script>

<style scoped lang="scss">
// Mirrors `CELEBRATION_FADE_MS` in `lib/rewardSequenceTimings.ts` — the prototype specifies the row's
// background flash as a 300ms settle. SCSS cannot import from TS, so this is the same unavoidable
// duplication the tracker carries, named to match.
$cart-row-arrive-ms: 300;

// No quantity UI of any kind: the board's steppers are all on hidden layers and dropping them is
// settled (plan, "Decisions locked").
.cart-sidebar-row {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem; // 24px

  // Divider between rows only, 32px clear each side. Scoped to this component rather than the list
  // container so it doesn't depend on scope-id propagation through nested component roots — every row
  // is an `AppCartSidebarRow`, and `:last-child` resolves against `.cart-sidebar__items`, which holds
  // nothing but rows. A row whose `v-if` is false renders a comment node, which `:last-child` ignores,
  // so a hidden trailing row can't leave a dangling divider.
  //
  // The board also draws a divider above YMAL on the mobile frame but not the desktop one; that is a
  // section separator rather than part of a row, so it belongs with the body restructure.
  &:not(:last-child) {
    padding-bottom: 2rem; // 32px
    border-bottom: 1px solid $cady-color-gray-96;
    margin-bottom: 2rem; // 32px
  }

  // Arrival flash. The prototype settles a paid row from `#f5eaf8` to white and a free one from
  // `#f0faf1` to `#f7fdf8`; the flyout's rows sit on white, so both settle to transparent and let the
  // panel show through rather than painting a near-white that would band against it.
  //
  // Animation rather than a transition, because the row is mounted already tinted — a transition needs
  // a change to interpolate from, and there is none when the class is present on first paint.
  &--arriving {
    animation: cart-sidebar-row-arrive #{$cart-row-arrive-ms}ms ease-out;
  }

  &--arriving-free {
    animation-name: cart-sidebar-row-arrive-free;
  }

  &__thumbnail {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 104px;
    height: 125px;
    overflow: hidden;
    border-radius: 2px;
    background-color: $cady-color-gray-96;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  // Grows to fill the row; on desktop the price is pushed to the bottom so it sits against the
  // thumbnail's base, which is what the board's `justify-between` does against the 125px thumbnail.
  &__content {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem; // 24px

    @include media-breakpoint-up(md) {
      align-self: stretch;
      justify-content: space-between;
      gap: 0;
    }
  }

  &__heading {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  &__text {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem; // 4px
  }

  // The board models the FREE pill's placement as two different structures, not one that wraps:
  // `16066:33310` is a column with the pill flush under the title (52px tall = 27 + 25, no gap), and
  // `15980:30203` is a row with a 16px gap. Expressing it as `flex-wrap` instead only *looked*
  // equivalent — wrapping is content-dependent, so a short reward name would have kept the pill on the
  // title's line with nothing between them. Stacked is the mobile base; the row layers up.
  //
  // `align-items: flex-start` is what keeps the pill at content width in the column; stretching is the
  // default and would run it the full row width.
  &__title-line {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    @include media-breakpoint-up(md) {
      flex-direction: row;
      align-items: center;
      gap: 1rem; // 16px
    }
  }

  &__title {
    font-size: $cady-type-h6-mobile-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-heading-line-height;
    color: $cady-color-black;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h6-desktop-size;
    }
  }

  // Figma `16066:33312` (mobile) / `15980:29681` (desktop).
  &__free-pill {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem 0.375rem; // 2px 6px
    border-radius: 4px;
    background-color: $cady-color-success-200;
    color: $cady-color-success-600;
    font-size: $cady-type-body-sm-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-body-sm-line-height;

    @include media-breakpoint-up(md) {
      padding: 0.125rem 0.5rem; // 2px 8px
      font-size: $cady-type-body-lg-size;
      line-height: $cady-type-body-lg-line-height;
    }
  }

  // `descriptionHtml` arrives as rich text, so the type has to reach the emitted elements rather than
  // only the wrapper.
  &__description {
    font-size: $cady-type-body-nm-size;
    line-height: $cady-type-body-nm-line-height;
    color: $cady-color-gray-600;

    :deep(p) {
      margin: 0;
      padding: 0;
    }

    :deep(ul),
    :deep(ol) {
      margin: 0;
      padding-left: 1.25rem;
    }
  }

  // The customer's selections — "Paper Type: Glossy (+$20.00)". Own class rather than `__description`
  // because the board sets these a step smaller. Plain text spans, so no `:deep` blocks.
  &__selection {
    font-size: $cady-type-body-sm-size;
    line-height: $cady-type-body-sm-line-height;
    color: $cady-color-gray-600;
  }

  // Owns the price treatment for whatever the caller slots in — size, weight and colour. Inheritance
  // crosses the scope boundary even though selectors don't, so a slotted `CartBundleItemPrice` or a
  // plain `<span>` both pick this up and only need to declare their departures from it.
  &__price {
    align-self: flex-end;
    font-size: $cady-type-h6-mobile-size;
    font-weight: $cady-type-heading-weight;
    line-height: $cady-type-heading-line-height;
    color: $cady-color-black;
    white-space: nowrap;

    @include media-breakpoint-up(md) {
      font-size: $cady-type-h6-desktop-size;
    }
  }
}

@keyframes cart-sidebar-row-arrive {
  from {
    background-color: #f5eaf8;
  }

  to {
    background-color: transparent;
  }
}

@keyframes cart-sidebar-row-arrive-free {
  from {
    background-color: #f0faf1;
  }

  to {
    background-color: transparent;
  }
}

// The flash is decoration on a row that is already readable, so it goes entirely.
@media (prefers-reduced-motion: reduce) {
  .cart-sidebar-row--arriving {
    animation: none;
  }
}
</style>
